import { useState } from 'react';
import { Upload, Play, Image as ImageIcon, Video, Filter, Grid, List, Share2, Download, Heart, Eye, X, ChevronLeft, ChevronRight } from 'lucide-react';

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  thumbnail?: string;
  title: string;
  description?: string;
  category: 'training' | 'competition' | 'highlights' | 'behind-scenes';
  tags: string[];
  uploadDate: string;
  likes: number;
  views: number;
  isLiked: boolean;
}

interface MediaGalleryProps {
  media: MediaItem[];
  isOwner: boolean;
}

const MediaGallery = ({ media, isOwner }: MediaGalleryProps) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const categories = [
    { id: 'all', label: 'Todo', count: media.length },
    { id: 'competition', label: 'Competencias', count: media.filter(m => m.category === 'competition').length },
    { id: 'training', label: 'Entrenamientos', count: media.filter(m => m.category === 'training').length },
    { id: 'highlights', label: 'Destacados', count: media.filter(m => m.category === 'highlights').length },
    { id: 'behind-scenes', label: 'Detrás de Cámaras', count: media.filter(m => m.category === 'behind-scenes').length }
  ];

  const filteredMedia = selectedCategory === 'all' 
    ? media 
    : media.filter(item => item.category === selectedCategory);

  const MediaCard = ({ item }: { item: MediaItem }) => (
    <div 
      className="group relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer"
      onClick={() => setSelectedMedia(item)}
    >
      <div className="aspect-video relative overflow-hidden">
        <img 
          src={item.thumbnail || item.url} 
          alt={item.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
          {item.type === 'video' && (
            <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs flex items-center">
              <Video size={12} className="mr-1" />
              Video
            </div>
          )}
          
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Play className="text-white" size={48} />
          </div>
        </div>

        {/* Stats */}
        <div className="absolute bottom-2 right-2 flex items-center space-x-2">
          <div className="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs flex items-center">
            <Eye size={12} className="mr-1" />
            {item.views}
          </div>
          <div className="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs flex items-center">
            <Heart size={12} className="mr-1" />
            {item.likes}
          </div>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1">{item.title}</h3>
        {item.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{item.description}</p>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 2).map((tag, index) => (
              <span key={index} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                #{tag}
              </span>
            ))}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(item.uploadDate).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  );

  const MediaListItem = ({ item }: { item: MediaItem }) => (
    <div 
      className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer"
      onClick={() => setSelectedMedia(item)}
    >
      <div className="w-24 h-16 rounded-lg overflow-hidden mr-4 flex-shrink-0">
        <img 
          src={item.thumbnail || item.url} 
          alt={item.title}
          className="w-full h-full object-cover"
        />
        {item.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Play className="text-white\" size={20} />
          </div>
        )}
      </div>
      
      <div className="flex-grow">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{item.title}</h3>
        {item.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-1">{item.description}</p>
        )}
        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center">
            <Eye size={12} className="mr-1" />
            {item.views} vistas
          </span>
          <span className="flex items-center">
            <Heart size={12} className="mr-1" />
            {item.likes} likes
          </span>
          <span>{new Date(item.uploadDate).toLocaleDateString()}</span>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <Share2 size={16} />
        </button>
        <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <Download size={16} />
        </button>
      </div>
    </div>
  );

  const MediaModal = () => {
    if (!selectedMedia) return null;

    const currentIndex = filteredMedia.findIndex(item => item.id === selectedMedia.id);
    const nextMedia = () => {
      const nextIndex = (currentIndex + 1) % filteredMedia.length;
      setSelectedMedia(filteredMedia[nextIndex]);
    };
    const prevMedia = () => {
      const prevIndex = (currentIndex - 1 + filteredMedia.length) % filteredMedia.length;
      setSelectedMedia(filteredMedia[prevIndex]);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
        <div className="relative max-w-6xl max-h-full w-full h-full flex items-center justify-center p-4">
          {/* Navigation */}
          <button 
            onClick={prevMedia}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
          >
            <ChevronLeft size={48} />
          </button>
          <button 
            onClick={nextMedia}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
          >
            <ChevronRight size={48} />
          </button>

          {/* Close button */}
          <button 
            onClick={() => setSelectedMedia(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X size={32} />
          </button>

          {/* Media content */}
          <div className="w-full h-full flex items-center justify-center">
            {selectedMedia.type === 'video' ? (
              <video 
                src={selectedMedia.url} 
                controls 
                className="max-w-full max-h-full"
                autoPlay
              />
            ) : (
              <img 
                src={selectedMedia.url} 
                alt={selectedMedia.title}
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>

          {/* Media info */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
            <div className="text-white">
              <h3 className="text-xl font-semibold mb-2">{selectedMedia.title}</h3>
              {selectedMedia.description && (
                <p className="text-gray-300 mb-3">{selectedMedia.description}</p>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-sm">
                  <span className="flex items-center">
                    <Eye size={16} className="mr-1" />
                    {selectedMedia.views} vistas
                  </span>
                  <span className="flex items-center">
                    <Heart size={16} className="mr-1" />
                    {selectedMedia.likes} likes
                  </span>
                  <span>{new Date(selectedMedia.uploadDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors">
                    <Heart size={16} />
                  </button>
                  <button className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors">
                    <Share2 size={16} />
                  </button>
                  <button className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors">
                    <Download size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const UploadModal = () => {
    if (!showUploadModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Subir Contenido</h2>
              <button 
                onClick={() => setShowUploadModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            <form className="space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Archivos
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    Arrastra archivos aquí o haz clic para seleccionar
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Soporta: JPG, PNG, MP4, MOV (máx. 100MB)
                  </p>
                  <input type="file" multiple accept="image/*,video/*" className="hidden" />
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Título
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Título del contenido"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descripción
                </label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Describe tu contenido..."
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Categoría
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent">
                  <option value="training">Entrenamiento</option>
                  <option value="competition">Competencia</option>
                  <option value="highlights">Destacados</option>
                  <option value="behind-scenes">Detrás de Cámaras</option>
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Etiquetas
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Separa las etiquetas con comas"
                />
              </div>

              {/* Privacy Settings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Privacidad
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input type="radio" name="privacy" value="public" className="mr-2" defaultChecked />
                    <span className="text-gray-700 dark:text-gray-300">Público - Visible para todos</span>
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="privacy" value="followers" className="mr-2" />
                    <span className="text-gray-700 dark:text-gray-300">Seguidores - Solo mis seguidores</span>
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="privacy" value="private" className="mr-2" />
                    <span className="text-gray-700 dark:text-gray-300">Privado - Solo yo</span>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Subir Contenido
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Galería Multimedia</h2>
          <p className="text-gray-600 dark:text-gray-400">{media.length} elementos</p>
        </div>
        {isOwner && (
          <button 
            onClick={() => setShowUploadModal(true)}
            className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
          >
            <Upload size={18} className="mr-2" />
            Subir Contenido
          </button>
        )}
      </div>

      {/* Filters and View Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                selectedCategory === category.id
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {category.label} ({category.count})
            </button>
          ))}
        </div>

        {/* View Controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              viewMode === 'grid'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Grid size={18} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              viewMode === 'list'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Media Grid/List */}
      {filteredMedia.length > 0 ? (
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
        }>
          {filteredMedia.map((item) => (
            viewMode === 'grid' ? (
              <MediaCard key={item.id} item={item} />
            ) : (
              <MediaListItem key={item.id} item={item} />
            )
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <ImageIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No hay contenido en esta categoría
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {isOwner ? 'Sube tu primer archivo multimedia' : 'Aún no hay contenido disponible'}
          </p>
          {isOwner && (
            <button 
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
            >
              <Upload size={18} className="mr-2" />
              Subir Contenido
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      <MediaModal />
      <UploadModal />
    </div>
  );
};

export default MediaGallery;